import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { proyectosAPI, subdivisionesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Building2, Calendar, Shield, ChevronDown, ChevronRight, Layers, Upload, Download } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import SubdivisionesManager from '../components/SubdivisionesManager';

const Proyectos = () => {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProyectos, setExpandedProyectos] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editingProyecto, setEditingProyecto] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    etapas: '',
    codigo_proyecto: '',
    tiene_subdivision: false,
    fecha_inicio_garantia_estructural: '',
    fecha_inicio_garantia_acabados: '',
    garantia_acabados_anios: 1,
    garantia_acabados_meses: 0,
    garantia_estructural_anios: 10,
    garantia_estructural_meses: 0,
  });
  const [saving, setSaving] = useState(false);
  const [importingSubdivisiones, setImportingSubdivisiones] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadProyectos();
  }, []);

  const loadProyectos = async () => {
    try {
      const response = await proyectosAPI.getAll();
      setProyectos(response.data || []);
    } catch (error) {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  // Importar subdivisiones desde Excel
  const handleImportSubdivisiones = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportingSubdivisiones(true);
    try {
      const response = await subdivisionesAPI.import(file);
      const result = response.data;
      
      toast.success(
        `Importación completada: ${result.agregadas} agregadas, ${result.actualizadas} actualizadas`,
        { duration: 5000 }
      );
      
      // Recargar proyectos para ver las nuevas subdivisiones
      loadProyectos();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al importar subdivisiones';
      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setImportingSubdivisiones(false);
      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Descargar plantilla de subdivisiones
  const handleDownloadPlantilla = async () => {
    try {
      const response = await subdivisionesAPI.downloadPlantilla();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_subdivisiones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleOpenCreate = () => {
    setEditingProyecto(null);
    setFormData({
      nombre: '',
      direccion: '',
      etapas: '',
      codigo_proyecto: '',
      tiene_subdivision: false,
      fecha_inicio_garantia_estructural: '',
      fecha_inicio_garantia_acabados: '',
      garantia_acabados_anios: 1,
      garantia_acabados_meses: 0,
      garantia_estructural_anios: 10,
      garantia_estructural_meses: 0,
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (proyecto) => {
    setEditingProyecto(proyecto);
    setFormData({
      nombre: proyecto.nombre || '',
      direccion: proyecto.direccion || '',
      etapas: Array.isArray(proyecto.etapas) ? proyecto.etapas.join(', ') : '',
      codigo_proyecto: proyecto.codigo_proyecto || '',
      tiene_subdivision: proyecto.tiene_subdivision || false,
      fecha_inicio_garantia_estructural: proyecto.fecha_inicio_garantia_estructural || '',
      fecha_inicio_garantia_acabados: proyecto.fecha_inicio_garantia_acabados || '',
      garantia_acabados_anios: proyecto.garantia_acabados_anios ?? 1,
      garantia_acabados_meses: proyecto.garantia_acabados_meses ?? 0,
      garantia_estructural_anios: proyecto.garantia_estructural_anios ?? 10,
      garantia_estructural_meses: proyecto.garantia_estructural_meses ?? 0,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre del proyecto es requerido');
      return;
    }

    setSaving(true);
    try {
      const etapasArray = formData.etapas
        .split(',')
        .map(e => e.trim())
        .filter(e => e !== '');

      const data = {
        nombre: formData.nombre,
        direccion: formData.direccion,
        etapas: etapasArray,
        codigo_proyecto: formData.codigo_proyecto || null,
        tiene_subdivision: formData.tiene_subdivision,
        fecha_inicio_garantia_estructural: formData.tiene_subdivision ? null : (formData.fecha_inicio_garantia_estructural || null),
        fecha_inicio_garantia_acabados: formData.tiene_subdivision ? null : (formData.fecha_inicio_garantia_acabados || null),
        garantia_acabados_anios: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_acabados_anios),
        garantia_acabados_meses: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_acabados_meses),
        garantia_estructural_anios: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_estructural_anios),
        garantia_estructural_meses: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_estructural_meses),
      };

      if (editingProyecto) {
        await proyectosAPI.update(editingProyecto.id, data);
        toast.success('Proyecto actualizado correctamente');
      } else {
        await proyectosAPI.create(data);
        toast.success('Proyecto creado correctamente');
      }

      setShowDialog(false);
      loadProyectos();
    } catch (error) {
      toast.error('Error al guardar el proyecto');
    } finally {
      setSaving(false);
    }
  };

  const formatGarantia = (anios, meses) => {
    const parts = [];
    if (anios > 0) parts.push(`${anios} año${anios > 1 ? 's' : ''}`);
    if (meses > 0) parts.push(`${meses} mes${meses > 1 ? 'es' : ''}`);
    return parts.length > 0 ? parts.join(' y ') : '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9d8272] mx-auto"></div>
          <p className="mt-4 text-sm text-[#5A5A5A]">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Proyectos
          </h1>
          <p className="text-sm sm:text-lg text-[#5A5A5A] mt-1">Gestiona los proyectos y sus garantías</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Botones de importación/exportación de subdivisiones */}
          <Button
            variant="outline"
            onClick={handleDownloadPlantilla}
            className="text-sm"
            data-testid="download-plantilla-subdivisiones"
          >
            <Download size={16} className="mr-2" />
            Plantilla
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importingSubdivisiones}
            className="text-sm"
            data-testid="import-subdivisiones"
          >
            <Upload size={16} className="mr-2" />
            {importingSubdivisiones ? 'Importando...' : 'Importar Subdivisiones'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportSubdivisiones}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button
            onClick={handleOpenCreate}
            className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Proyecto
          </Button>
        </div>
      </div>

      {/* Lista de Proyectos */}
      <div className="grid gap-4">
        {proyectos.map((proyecto) => (
          <Card key={proyecto.id} className="bg-white border border-[#E8E8E7] shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 size={20} className="text-[#9d8272]" />
                    <h3 className="font-semibold text-lg text-[#2D2D2D]">{proyecto.nombre}</h3>
                    {proyecto.codigo_proyecto && (
                      <Badge variant="secondary" className="text-xs bg-[#E8F4E8] text-[#2D2D2D]">
                        {proyecto.codigo_proyecto}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-[#5A5A5A] mb-3">{proyecto.direccion || 'Sin dirección'}</p>
                  
                  {/* Etapas */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {(proyecto.etapas || []).map((etapa, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {etapa}
                      </Badge>
                    ))}
                  </div>

                  {/* Garantías */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#F5F5F5] p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield size={16} className="text-[#8c926f]" />
                      <div>
                        <p className="text-xs text-[#888888]">Garantía Acabados</p>
                        <p className="text-sm font-medium text-[#2D2D2D]">
                          {formatGarantia(proyecto.garantia_acabados_anios ?? 1, proyecto.garantia_acabados_meses ?? 0)}
                        </p>
                        {proyecto.fecha_inicio_garantia_acabados && (
                          <p className="text-[10px] text-[#888888]">Desde: {proyecto.fecha_inicio_garantia_acabados}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield size={16} className="text-[#9d8272]" />
                      <div>
                        <p className="text-xs text-[#888888]">Garantía Estructural</p>
                        <p className="text-sm font-medium text-[#2D2D2D]">
                          {formatGarantia(proyecto.garantia_estructural_anios ?? 10, proyecto.garantia_estructural_meses ?? 0)}
                        </p>
                        {proyecto.fecha_inicio_garantia_estructural && (
                          <p className="text-[10px] text-[#888888]">Desde: {proyecto.fecha_inicio_garantia_estructural}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEdit(proyecto)}
                    className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white"
                  >
                    <Edit size={16} className="mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedProyectos(prev => ({ ...prev, [proyecto.id]: !prev[proyecto.id] }))}
                    className="text-[#5A5A5A]"
                  >
                    {expandedProyectos[proyecto.id] ? <ChevronDown size={16} className="mr-1" /> : <ChevronRight size={16} className="mr-1" />}
                    <Layers size={14} className="mr-1" />
                    Subdivisiones
                  </Button>
                </div>
              </div>

              {/* Subdivisiones expandibles */}
              {expandedProyectos[proyecto.id] && (
                <div className="mt-4 pt-4 border-t border-[#E8E8E7]">
                  <SubdivisionesManager proyecto={proyecto} onUpdate={loadProyectos} />
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {proyectos.length === 0 && (
          <Card className="bg-white border border-[#E8E8E7]">
            <CardContent className="p-8 text-center">
              <Building2 size={48} className="mx-auto text-[#c5b8a2] mb-4" />
              <p className="text-[#5A5A5A]">No hay proyectos registrados</p>
              <Button
                onClick={handleOpenCreate}
                className="mt-4 bg-[#9d8272] hover:bg-[#8a7162] text-white"
              >
                Crear primer proyecto
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog para Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#2D2D2D]">
              {editingProyecto ? 'Editar Proyecto' : 'Nuevo Proyecto'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Datos básicos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-sm">Nombre del Proyecto *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="border-[#c5b8a2] mt-1"
                  placeholder="Ej: Residencial Los Pinos"
                />
              </div>

              <div>
                <Label className="text-sm">Código de Proyecto</Label>
                <Input
                  value={formData.codigo_proyecto}
                  onChange={(e) => setFormData({ ...formData, codigo_proyecto: e.target.value.slice(0, 20) })}
                  className="border-[#c5b8a2] mt-1"
                  placeholder="Ej: PROY-001"
                  maxLength={20}
                />
              </div>

              <div>
                <Label className="text-sm">Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  className="border-[#c5b8a2] mt-1"
                  placeholder="Ej: Av. Principal 123"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm">Etapas (separadas por coma)</Label>
              <Input
                value={formData.etapas}
                onChange={(e) => setFormData({ ...formData, etapas: e.target.value })}
                className="border-[#c5b8a2] mt-1"
                placeholder="Ej: Etapa 1, Etapa 2, Etapa 3"
              />
            </div>

            {/* Checkbox tiene subdivisión */}
            <div className="flex items-center space-x-2 p-3 bg-[#F5F5F5] rounded-lg">
              <Checkbox
                id="tiene_subdivision"
                checked={formData.tiene_subdivision}
                onCheckedChange={(checked) => setFormData({ ...formData, tiene_subdivision: checked })}
              />
              <Label htmlFor="tiene_subdivision" className="text-sm cursor-pointer">
                El proyecto tiene subdivisiones
              </Label>
            </div>

            {formData.tiene_subdivision && (
              <p className="text-xs text-[#888888] bg-yellow-50 p-2 rounded border border-yellow-200">
                ⚠️ Las garantías se configurarán a nivel de subdivisión
              </p>
            )}

            {/* Garantía de Acabados */}
            <div className={`bg-[#F5F5F5] p-4 rounded-lg ${formData.tiene_subdivision ? 'opacity-50' : ''}`}>
              <Label className="text-sm font-semibold text-[#8c926f] flex items-center gap-2 mb-3">
                <Shield size={16} />
                Garantía de Acabados
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#888888]">Años (0-10)</Label>
                  <Select
                    value={String(formData.garantia_acabados_anios)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_acabados_anios: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} año{i !== 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Meses (0-11)</Label>
                  <Select
                    value={String(formData.garantia_acabados_meses)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_acabados_meses: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} mes{i !== 1 ? 'es' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_acabados}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_acabados: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-10"
                    disabled={formData.tiene_subdivision}
                  />
                </div>
              </div>
            </div>

            {/* Garantía Estructural */}
            <div className={`bg-[#F5F5F5] p-4 rounded-lg ${formData.tiene_subdivision ? 'opacity-50' : ''}`}>
              <Label className="text-sm font-semibold text-[#9d8272] flex items-center gap-2 mb-3">
                <Shield size={16} />
                Garantía Estructural
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#888888]">Años (0-10)</Label>
                  <Select
                    value={String(formData.garantia_estructural_anios)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_estructural_anios: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} año{i !== 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Meses (0-11)</Label>
                  <Select
                    value={String(formData.garantia_estructural_meses)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_estructural_meses: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} mes{i !== 1 ? 'es' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_estructural}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_estructural: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-10"
                    disabled={formData.tiene_subdivision}
                  />
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E8E8E7]">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
              >
                {saving ? 'Guardando...' : editingProyecto ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Proyectos;
